import React, { useCallback } from 'react';
import { useButtonPress } from '../../hooks/useButtonPress';

/**
 * Icon-only control with the same press / disabled / loading feedback as Button.
 */
export default function IconButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  className = '',
  style = {},
  'aria-label': ariaLabel,
  title,
}) {
  const { pressed, pressHandlers } = useButtonPress({ disabled, loading });

  const handleClick = useCallback(
    (event) => {
      if (disabled || loading) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    },
    [disabled, loading, onClick]
  );

  const spinner = (
    <svg
      className="app-btn-pill__spinner"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );

  const content =
    loading && React.isValidElement(children)
      ? React.cloneElement(children, {
          className: [children.props.className, 'app-btn-pill__icon--spin'].filter(Boolean).join(' '),
        })
      : loading
        ? spinner
        : children;

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      title={title}
      data-pressed={pressed ? 'true' : undefined}
      data-loading={loading ? 'true' : undefined}
      className={`app-icon-btn ${loading ? 'app-icon-btn--loading' : ''} ${className}`.trim()}
      style={style}
      {...pressHandlers}
    >
      {content}
    </button>
  );
}
